import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Brain, BarChart3, Bot, ShieldAlert, Activity,
  Search, Filter, Database, Settings, Gauge, ChevronDown, ChevronRight,
  Workflow, ClipboardList, Package, MonitorCheck
} from 'lucide-react';

const Sidebar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const [expandedItems, setExpandedItems] = useState({ 'AppSupport': true });

  const menuItems = [
    {
      name: 'AppSupport',
      icon: MonitorCheck,
      hasSubmenu: true,
      submenu: [
        { name: 'Inventory', path: '/appsupport/inventory', icon: Package },
        { name: 'Operator Dashboard', path: '/appsupport/dashboard', icon: Gauge },
        { name: 'Application Intake', path: '/appsupport/intake', icon: ClipboardList },
        { name: 'Process Registry', path: '/appsupport/registry', icon: Workflow }
      ]
    },
    {
      name: 'Agentic AI',
      icon: Bot,
      hasSubmenu: true,
      submenu: [
        { name: 'Incidents', path: '/incidents' },
        { name: 'Problems', path: '/problems' }
      ]
    },
    {
      name: 'Anomaly Detection',
      icon: Activity,
      hasSubmenu: true,
      submenu: [
        { name: 'Detection Rules', path: '/detection-rules' },
        { name: 'Anomalies', path: '/anomalies' }
      ]
    },
    {
      name: 'Event Analysis',
      icon: Search,
      hasSubmenu: true,
      submenu: [
        { name: 'Event Logs', path: '/event-logs' },
        { name: 'Noise Filters', path: '/noise-filters' }
      ]
    },
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, hasSubmenu: false },
    { name: 'Model Studio', path: '/model-studio', icon: Brain, hasSubmenu: false },
    { name: 'Remediation', path: '/remediation', icon: ShieldAlert, hasSubmenu: false },
    { name: 'Data Pipeline', path: '/data-pipeline', icon: Database, hasSubmenu: false },
    { name: 'Integrations', path: '/integrations', icon: Settings, hasSubmenu: false },
    { name: 'Analytics', path: '/analytics', icon: BarChart3, hasSubmenu: false }
  ];

  const toggleSubmenu = (itemName) => {
    setExpandedItems(prev => ({ ...prev, [itemName]: !prev[itemName] }));
  };

  const isActive = (path) => location.pathname === path;
  const isParentActive = (submenu) => submenu && submenu.some(s => location.pathname === s.path);

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-card rounded-lg shadow-lg border border-border"
        data-testid="sidebar-mobile-toggle"
        aria-label="Toggle sidebar"
      >
        {isOpen ? <span className="text-foreground">&#10005;</span> : <span className="text-foreground">&#9776;</span>}
      </button>

      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-card border-r border-border
          transform transition-transform duration-200 ease-in-out flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-border">
          <h1
            className="text-lg font-semibold text-foreground tracking-tight"
            style={{ fontFamily: 'var(--font-display)' }}
            data-testid="sidebar-title"
          >
            Juno HawkAI
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">AppSupport AI Agent</p>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const expanded = expandedItems[item.name];
            const parentActive = isParentActive(item.submenu);

            return (
              <div key={item.name}>
                {item.hasSubmenu ? (
                  <button
                    onClick={() => toggleSubmenu(item.name)}
                    className={`
                      w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md
                      transition-colors duration-150 text-sm
                      ${parentActive
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                    data-testid={`sidebar-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span>{item.name}</span>
                    </div>
                    {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={`
                      flex items-center gap-2.5 px-3 py-2 rounded-md
                      transition-colors duration-150 text-sm
                      ${isActive(item.path)
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }
                    `}
                    data-testid={`sidebar-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                )}

                {item.hasSubmenu && expanded && item.submenu && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l border-border pl-3">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        className={`
                          flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs
                          transition-colors duration-150
                          ${isActive(subItem.path)
                            ? 'bg-accent text-accent-foreground font-medium'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }
                        `}
                        data-testid={`sidebar-nav-${subItem.name.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        {subItem.icon && <subItem.icon className="h-3.5 w-3.5 flex-shrink-0" />}
                        <span>{subItem.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 bg-[hsl(var(--status-ok))] rounded-full"></span>
            <span className="text-muted-foreground">Platform Active</span>
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
